import { openDB, type IDBPDatabase } from 'idb'
import type { BenchmarkRun } from '@/types'
import { sanitizeForExport } from './security-utils'
import { getRunStats } from './metrics'

const DB_NAME = 'bedrock-diag-results'
const STORE_NAME = 'runs'
const DB_VERSION = 1

class ResultsStore {
  private dbPromise: Promise<IDBPDatabase> | null = null

  private getDb(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'id' })
          }
        },
      })
    }
    return this.dbPromise
  }

  async isAvailable(): Promise<boolean> {
    try {
      await this.getDb()
      return true
    } catch {
      return false
    }
  }

  async saveRun(run: BenchmarkRun): Promise<void> {
    const cleanRun = {
      ...run,
      stats: [], // Stats are recomputed on load — no need to persist
      profiles: run.profiles.map(p => {
        if ('imageAttachments' in p) {
          const { imageAttachments, ...rest } = p as any
          return { ...rest, imageCount: imageAttachments?.length || 0 }
        }
        return p
      }),
    }
    const sanitized = sanitizeForExport(cleanRun)
    const db = await this.getDb()
    await db.put(STORE_NAME, sanitized)
  }

  async listRuns(): Promise<BenchmarkRun[]> {
    const db = await this.getDb()
    const runs: BenchmarkRun[] = await db.getAll(STORE_NAME)
    return runs.map(run => ({
      ...run,
      stats: (!run.stats || run.stats.length === 0) ? getRunStats(run) : run.stats,
    }))
  }

  async loadRun(runId: string): Promise<BenchmarkRun | undefined> {
    const db = await this.getDb()
    const run = await db.get(STORE_NAME, runId) as BenchmarkRun | undefined
    if (run && (!run.stats || run.stats.length === 0)) {
      run.stats = getRunStats(run)
    }
    return run
  }

  async deleteRun(runId: string): Promise<void> {
    const db = await this.getDb()
    await db.delete(STORE_NAME, runId)
  }

  exportRunAsJson(run: BenchmarkRun): string {
    return JSON.stringify(sanitizeForExport(run), null, 2)
  }
}

export const resultsStore = new ResultsStore()
